// FindSideFromTwoSidesAndAdjacentAngleTask.js
// Dane: znane boki a, b oraz kąt α przyległy do b (między b i c). Oblicz WSZYSTKIE możliwe długości boku c.
// Równanie: a^2 = b^2 + c^2 - 2 b c cos α  =>  c^2 - 2 b cos α c + (b^2 - a^2) = 0.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, { parseFractionLikeInput as parseNum } from "../../../../components/inputs/FractionInput";


/* ===== helpers ===== */
const angles = [30, 45, 60, 120, 135, 150]; // bez 90°
const ri = (l, h) => l + Math.floor(Math.random() * (h - l + 1));
const toRad = (d) => (d * Math.PI) / 180;

const sinNum = (deg) =>
  deg === 30 ? 0.5 :
  deg === 45 ? Math.SQRT1_2 :
  deg === 60 ? Math.sqrt(3)/2 :
  deg === 120 ? Math.sqrt(3)/2 :
  deg === 135 ? Math.SQRT1_2 :
  deg === 150 ? 0.5 :
  Math.sin(toRad(deg));

const cosNum = (deg) =>
  deg === 30 ? Math.sqrt(3)/2 :
  deg === 45 ? Math.SQRT2/2 :
  deg === 60 ? 0.5 :
  deg === 120 ? -0.5 :
  deg === 135 ? -Math.SQRT2/2 :
  deg === 150 ? -Math.sqrt(3)/2 :
  Math.cos(toRad(deg));

const cosLatex = (deg) => ({
  30:"\\tfrac{\\sqrt{3}}{2}",45:"\\tfrac{\\sqrt{2}}{2}",60:"\\tfrac{1}{2}",
  120:"-\\tfrac{1}{2}",135:"-\\tfrac{\\sqrt{2}}{2}",150:"-\\tfrac{\\sqrt{3}}{2}"
}[deg] ?? "");

const sinLatex = (deg) => ({
  30:"\\tfrac{1}{2}",45:"\\tfrac{\\sqrt{2}}{2}",60:"\\tfrac{\\sqrt{3}}{2}",
  120:"\\tfrac{\\sqrt{3}}{2}",135:"\\tfrac{\\sqrt{2}}{2}",150:"\\tfrac{1}{2}"
}[deg] ?? "");

const fmt2 = (x) => (Math.round(x*100)/100).toFixed(2).replace(".", "{,}");
const pp   = (raw) => String(raw ?? "").trim().replace(/\u2212/g,"-").replace(/\s+/g,"").replace(",", ".");

/* ===== generator ===== */
// 1) GENERATOR — gwarantuje dodatnie rozwiązania
export function generateTask() {
  let a, b, alpha, solutions;

  for (let t = 0; t < 300; t++) {
    b = ri(6, 18);
    alpha = angles[Math.floor(Math.random() * angles.length)];

    // a dobieramy tak, aby istniał sensowny trójkąt
    if (alpha > 90) {
      // przy rozwartym: a (naprzeciw α) musi być największy → weź a > b
      a = ri(b + 1, b + 10);
    } else {
      // przy ostrym: czasem chcemy 2 dodatnie pierwiastki (gdy a < b), czasem 1
      a = Math.random() < 0.6 ? ri(6, Math.max(6, b - 1)) : ri(b, b + 10);
    }

    const s = sinNum(alpha);
    const k = cosNum(alpha);
    const under = a * a - (b * b) * (s * s);        // a^2 - b^2 sin^2 α
    if (under <= 0) continue;                       // brak rozwiązań → losuj dalej

    const root = Math.sqrt(under);
    const c1 = b * k + root;
    const c2 = b * k - root;

    // bierzemy tylko dodatnie c (fizycznie sensowne długości)
    solutions = [c1, c2].filter((x) => x > 0).sort((x, y) => x - y);
    if (solutions.length === 0) continue;           // nic dodatniego → losuj dalej

    // mamy gotowy przypadek
    return {
      type: "FindSideFromTwoSidesAndAdjacentAngleTask",
      data: {
        type: "FindSideFromTwoSidesAndAdjacentAngleTask",
        a, b, alpha,
        solutions,
        hasTwo: solutions.length === 2,
      },
    };
  }

}


/* ===== treść ===== */
export function renderTask({ data }) {
  const { a, b, alpha, hasTwo } = data;
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        W trójkącie <InlineMath math={"ABC"} /> dane są boki{" "}
        <InlineMath math={`a=${a}`} /> i <InlineMath math={`b=${b}`} /> oraz kąt{" "}
        <InlineMath math={`\\alpha=${alpha}^\\circ`} /> przyległy do boku{" "}
        <InlineMath math={"b"} /> (między bokami <InlineMath math={"b"} /> i <InlineMath math={"c"} />).
        Oblicz <strong>wszystkie możliwe</strong> długości boku <InlineMath math={"c"} />.
      </div>
      <div style={{ marginTop: 6, opacity: 0.9 }}>
        Podaj wynik(i) z dokładnością do <InlineMath math={"0{,}01"} />{hasTwo ? " (będą dwie wartości, kolejność dowolna)" : ""}.
      </div>
    </div>
  );
}

/* ===== input ===== */
// ⬇️ INPUT: pola pokazujemy dopiero po wyborze 1/2; przy "1" etykieta = "c"
export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = {
    declared: "",
    c1: { raw: "" },
    c2: { raw: "" },
    ...(userAnswer ?? {}),
  };

  const group = { display: "grid", gap: "6px" };

  const handleDeclare = (e) => {
    const declared = e.target.value;
    onChange({
      ...val,
      declared,
      c1: val.c1 ?? { raw: "" },
      c2: declared === "2" ? (val.c2 ?? { raw: "" }) : { raw: "" }, // przy 1 czyścimy c2
    });
  };

  const handleC1 = (v) => onChange({ ...val, c1: v });
  const handleC2 = (v) => onChange({ ...val, c2: v });

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span>Ile rozwiązań?</span>
        <SelectInput
          value={val.declared}
          onChange={handleDeclare}
          disabled={disabled}
          options={[
            { value: "1", label: "1" },
            { value: "2", label: "2" },
          ]}
        />
      </div>

      {(val.declared === "1" || val.declared === "2") && (
        <>
          <div style={group}>
            <FractionInput
              value={val.c1?.raw ?? ""}      // zawsze string
              onChange={handleC1}
              disabled={disabled}
              label={val.declared === "2" ? "c₁" : "c"}
              placeholder="np. 12,34"
            />
          </div>

          {val.declared === "2" && (
            <div style={group}>
              <FractionInput
                value={val.c2?.raw ?? ""}    // zawsze string
                onChange={handleC2}
                disabled={disabled}
                label="c₂"
                placeholder="np. 7,89"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}




/* ===== check ===== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  // oczekiwana liczba rozwiązań (pozytywnych c) z generatora
  const expectedCount = data.hasTwo ? "2" : "1";
  const exp = data.solutions.slice().sort((a, b) => a - b);

  // deklaracja użytkownika musi istnieć i zgadzać się z expectedCount
  const declared = String(userAnswer.declared || "");
  if (declared !== "1" && declared !== "2") return false;
  if (declared !== expectedCount) return false;

  // pomocniczo: wyciągamy liczby z pól
  const toNum = (x) => {
    const raw = typeof x === "object" ? x?.raw : x;
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const v = parseNum(s.replace(",", "."));
    return Number.isFinite(v) ? v : null;
  };

  const x1 = toNum(userAnswer.c1);
  const x2 = toNum(userAnswer.c2);
  const provided = [x1, x2].filter((v) => v != null);

  const tol = 0.01;

  // musimy mieć dokładnie tyle wartości, ile zadeklarowano
  if (declared === "1") {
    if (provided.length !== 1) return false;
    return Math.abs(provided[0] - exp[0]) <= tol;
  } else {
    if (provided.length !== 2) return false;
    const got = provided.slice().sort((a, b) => a - b);
    return (
      Math.abs(got[0] - exp[0]) <= tol &&
      Math.abs(got[1] - exp[1]) <= tol
    );
  }
}

/* ===== answer ===== */
// 2) ODPOWIEDŹ — pokazuj tylko dodatnie i tyle, ile faktycznie jest
export function renderAnswer({ data }) {
  const { solutions, hasTwo } = data;
  if (hasTwo) {
    const [x, y] = solutions;
    return (
      <div style={{ marginTop: 6, marginBottom: 6 }}>
        <InlineMath math={`c_{1} \\approx ${fmt2(x)},\\quad c_{2} \\approx ${fmt2(y)}`} />
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`c \\approx ${fmt2(solutions[0])}`} />
    </div>
  );
}

/* ===== explanation ===== */
export function renderExplanation({ data }) {
  const { a, b, alpha, solutions, hasTwo } = data;

  // kosinus: ładny LaTeX + wartość liczbowa
  const cosL = cosLatex(alpha);
  const kcos = cosNum(alpha);

  // współczynniki liczbowe (użyte przy Δ i pierwiastkach)
  const A = 1;
  const Bnum = -2 * b * kcos;
  const Cnum = b * b - a * a;

  // formatery
  const fmt = (x) => {
    const r = Math.round(x);
    return Math.abs(x - r) < 1e-9 ? `${r}` : (Math.round(x * 1000) / 1000).toString().replace(".", "{,}");
  };
  const signTerm = (x) => (x >= 0 ? "+" : "−");
  const absLatex = (x) => fmt(Math.abs(x));

  // B w postaci z pierwiastkiem (dokładnie) + k w B^2 = k b^2 (k ∈ {1,2,3})
  let k;              // współczynnik w Δ = k b^2 − 4C
  let BmagLatex;      // |B|, np. "17\\sqrt{2}" lub "17"
  if (alpha === 30 || alpha === 150) { k = 3; BmagLatex = `${b}\\sqrt{3}`; }
  else if (alpha === 45 || alpha === 135) { k = 2; BmagLatex = `${b}\\sqrt{2}`; }
  else /* 60 lub 120 */                { k = 1; BmagLatex = `${b}`; }

  const Bpos = Bnum > 0;
  const BexactInEq = `${Bpos ? "+" : "−"} ${BmagLatex}`;   // do linii z równaniem
  const BexactDisplay = `${Bpos ? "" : "−"}${BmagLatex}`;  // do "B = ..."

  // równanie po uproszczeniu (zostawiamy dokładne B z pierwiastkiem)
  const eqSimplifiedLatex =
    `c^{2} ${BexactInEq}\\,c ${signTerm(Cnum)} ${absLatex(Cnum)} = 0`;

  // Δ dokładnie i liczbowo
  const DeltaExact = k * b * b - 4 * Cnum;        // k b^2 − 4C
  const Delta = Bnum * Bnum - 4 * A * Cnum;       // liczbowo (powinno = DeltaExact)
  const sqrtDelta = Math.sqrt(Math.max(0, Delta));

  // pierwiastki (liczbowe do przybliżeń)
  const c1 = (-Bnum + sqrtDelta) / (2 * A);
  const c2 = (-Bnum - sqrtDelta) / (2 * A);

  const c1f = fmt2(c1);
  const c2f = fmt2(c2);

  const negative =
    (c1 <= 0 && c2 > 0) ? `c_{1} \\approx ${fmt2(c1)}` :
    (c2 <= 0 && c1 > 0) ? `c_{2} \\approx ${fmt2(c2)}` : "";

  return (
    <div style={{ textAlign: "left", marginTop: 8, lineHeight: 1.7 }}>
      {/* Krok 1 */}
      <div><strong>Krok 1.</strong> Twierdzenie cosinusów (od razu podstawiamy dane):</div>
      <BlockMath math={`a^{2} = b^{2} + c^{2} - 2bc\\cos\\alpha`} />
      <BlockMath math={`\\cos\\alpha = ${cosL}`} />
      <BlockMath math={`${a}^{2} = ${b}^{2} + c^{2} - 2\\cdot ${b}\\cdot c\\cdot (${cosL})`} />

      {/* Krok 2 – najpierw liczymy w równaniu, potem A,B,C */}
      <div><strong>Krok 2.</strong> Porządkujemy do postaci <InlineMath math={"f(c)=0"} /> i upraszczamy:</div>
      <BlockMath math={`c^{2} - 2\\cdot ${b}\\cdot (${cosL})\\,c + (${b}^{2} - ${a}^{2}) = 0`} />
      <BlockMath math={eqSimplifiedLatex} />
      <BlockMath math={`\\Rightarrow\\; A=1,\\quad B= ${BexactDisplay},\\quad C=${fmt(Cnum)}`} />

      {/* Krok 3 – delta; na końcu pokazujemy też √Δ symbolicznie */}
      <div><strong>Krok 3.</strong> Liczymy deltę:</div>
      <BlockMath math={`\\Delta = B^{2} - 4AC`} />
      <BlockMath math={`= (${BexactDisplay})^{2} - 4\\cdot 1\\cdot (${fmt(Cnum)})`} />
      <BlockMath math={`= ${k}\\,${b}^{2} - 4\\cdot (${fmt(Cnum)})`} />
      <BlockMath math={`\\Rightarrow\\; \\Delta = ${DeltaExact},\\quad \\sqrt{\\Delta} = \\sqrt{${DeltaExact}}\\,\\approx\\, ${fmt(sqrtDelta)}`} />

      {/* Krok 4 – pierwiastki; przybliżenia na końcu */}
      <div><strong>Krok 4.</strong> Wzory na pierwiastki równania:</div>
      <BlockMath math={`c_{1,2} = \\dfrac{-B \\pm \\sqrt{\\Delta}}{2A}`} />
      <BlockMath math={`= \\dfrac{-(${BexactDisplay}) \\pm \\sqrt{${DeltaExact}}}{2}`} />
      <BlockMath math={`\\Rightarrow\\; c_{1} \\approx ${c1f},\\quad c_{2} \\approx ${c2f}`} />

      {/* Komentarz: odrzucamy ujemną długość, jeśli występuje */}
      {(c1 <= 0 || c2 <= 0) && (
        <div>
          <em>
            Jedno z rozwiązań jest ujemne ({<InlineMath math={negative} />}).
            Długość boku musi być dodatnia — <strong>odrzucamy</strong> ten wynik.
          </em>
        </div>
      )}

      {/* Odpowiedź */}
      <div style={{ marginTop: 6 }}>
        <strong>Odpowiedź:</strong>{" "}
        {hasTwo
          ? <BlockMath math={`c_{1} \\approx ${fmt2(solutions[0])},\\; c_{2} \\approx ${fmt2(solutions[1])}`} />
          : <BlockMath math={`c \\approx ${fmt2(solutions[0])}`} />
        }
      </div>
    </div>
  );
}






/* ===== user answer preview ===== */
function pretty(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.declared) {
    return <i>(najpierw wybierz liczbę rozwiązań)</i>;
  }

  const dec = String(userAnswer.declared);
  const c1r = typeof userAnswer.c1 === "object" ? userAnswer.c1?.raw : userAnswer.c1;
  const c2r = typeof userAnswer.c2 === "object" ? userAnswer.c2?.raw : userAnswer.c2;

  if (dec === "1") {
    if (!c1r || String(c1r).trim() === "") return <i>(brak odpowiedzi)</i>;
    return <InlineMath math={`c = ${pretty(c1r)}`} />;
  }

  // dec === "2"
  const left = c1r && String(c1r).trim() !== "" ? pretty(c1r) : "—";
  const right = c2r && String(c2r).trim() !== "" ? pretty(c2r) : "—";
  return <InlineMath math={`c_{1} = ${left}\\,,\\; c_{2} = ${right}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
